/**
 * --------------------------------------------------------------------------
 * NJ : Tooltip.js
 * --------------------------------------------------------------------------
 */

import {
  Data, EventHandler, Manipulator, SelectorEngine, Util
} from 'nj-utils';
import { coreConfig, eventsName } from 'nj-components/core.config';
import AbstractComponent from 'nj-components/AbstractComponent';
import Popper from 'popper.js';

/**
 * ------------------------------------------------------------------------
 * Constants
 * ------------------------------------------------------------------------
 */

const NAME               = `${coreConfig.KEY_PREFIX}-tooltip`;
const DATA_KEY           = `${coreConfig.KEY_PREFIX}.tooltip`;
const EVENT_KEY          = `.${DATA_KEY}`;

const CLASS_NAME = {
  default      : `${coreConfig.KEY_PREFIX}-tooltip`,
  inner        : `${coreConfig.KEY_PREFIX}-tooltip__inner`,
  arrow        : `${coreConfig.KEY_PREFIX}-tooltip__arrow`,
  withoutArrow : `${coreConfig.KEY_PREFIX}-tooltip--without-arrow`,
  fade         : 'fade',
  show         : 'show'
};

const NJCLS_PREFIX_REGEX = new RegExp(`(^|\\s)${CLASS_NAME.default}\\S+`, 'g');

const DEFAULT_TYPE = {
  animation         : 'boolean',
  template          : 'string',
  title             : '(string|element|function)',
  trigger           : 'string',
  delay             : '(number|object)',
  html              : 'boolean',
  selector          : '(string|boolean)',
  placement         : '(string|function)',
  offset            : '(number|string)',
  container         : '(string|element|boolean)',
  fallbackPlacement : '(string|array)',
  boundary          : '(string|element)',
  arrow             : 'boolean'
};

const ATTACHMENT_MAP = {
  AUTO   : 'auto',
  TOP    : 'top',
  RIGHT  : 'right',
  BOTTOM : 'bottom',
  LEFT   : 'left'
};

export const DEFAULT_OPTIONS = {
  animation         : true,
  template          : `<div class="${CLASS_NAME.default}" role="tooltip">` +
  `<div class="${CLASS_NAME.arrow}"></div>` +
  `<div class="${CLASS_NAME.inner}"></div></div>`,
  trigger           : 'hover focus',
  title             : '',
  delay             : 0,
  html              : false,
  selector          : false,
  placement         : 'top',
  offset            : 0,
  container         : false,
  fallbackPlacement : 'flip',
  boundary          : 'scrollParent',
  arrow             : true
};

const HOVER_STATE = {
  show : 'show',
  out  : 'out'
};

const EVENT = {
  hide       : `${eventsName.hide}${EVENT_KEY}`,
  hidden     : `${eventsName.hidden}${EVENT_KEY}`,
  show       : `${eventsName.show}${EVENT_KEY}`,
  shown      : `${eventsName.shown}${EVENT_KEY}`,
  inserted   : `${eventsName.inserted}${EVENT_KEY}`,
  click      : `${eventsName.click}${EVENT_KEY}`,
  focusin    : `${eventsName.focusin}${EVENT_KEY}`,
  focusout   : `${eventsName.focusout}${EVENT_KEY}`,
  mouseenter : `${eventsName.mouseenter}${EVENT_KEY}`,
  mouseleave : `${eventsName.mouseleave}${EVENT_KEY}`
};

export const SELECTOR = {
  default      : `.${CLASS_NAME.default}`,
  inner        : `.${CLASS_NAME.inner}`,
  arrow        : `.${CLASS_NAME.arrow}`
};

const TRIGGER = {
  hover  : 'hover',
  focus  : 'focus',
  click  : 'click',
  manual : 'manual'
};


/**
 * ------------------------------------------------------------------------
 * Class Definition
 * ------------------------------------------------------------------------
 */

class Tooltip extends AbstractComponent {
  constructor(element, config) {
    super(Tooltip, element);

    this.isEnabled     = true;
    this.timeout       = 0;
    this.hoverState    = '';
    this.activeTrigger = {};
    this.popper        = null;
    this.element       = element;
    this.config        = this.getConfig(config);
    this.tip           = null;

    this.setListeners();
    Data.setData(element, DATA_KEY, this);
  }

  enable() {
    this.isEnabled = true;
  }

  disable() {
    this.isEnabled = false;
  }

  toggleEnabled() {
    this.isEnabled = !this.isEnabled;
  }

  toggle(event) {
    if (!this.isEnabled) {
      return;
    }

    if (event) {
      const dataKey = DATA_KEY;
      let context = Data.getData(event.delegateTarget, dataKey);

      if (!context) {
        context = new this.constructor(
          event.delegateTarget,
          this.getDelegateConfig()
        );
        Data.setData(event.delegateTarget, dataKey, context);
      }

      context.activeTrigger.click = !context.activeTrigger.click;

      if (context.isWithActiveTrigger()) {
        context.enter(null, context);
      } else {
        context.leave(null, context);
      }
    } else {
      if (this.getTipElement().classList.contains(CLASS_NAME.show)) {
        this.leave(null, this);
        return;
      }

      this.enter(null, this);
    }
  }

  dispose() {
    clearTimeout(this.timeout);

    Data.removeData(this.element, DATA_KEY);

    EventHandler.off(this.element, EVENT_KEY);
    EventHandler.off(SelectorEngine.closest(this.element, '.modal'), 'hide.nj.modal');

    if (this.tip) {
      this.tip.parentNode.removeChild(this.tip);
    }

    this.isEnabled     = null;
    this.timeout       = null;
    this.hoverState    = null;
    this.activeTrigger = null;

    if (this.popper !== null) {
      this.popper.destroy();
    }

    this.popper  = null;
    this.element = null;
    this.config  = null;
    this.tip     = null;
  }

  show() {
    if (this.element.style.display === 'none') {
      throw new Error('Please use show on visible elements');
    }

    if (this.isWithContent() && this.isEnabled) {
      const showEvent = EventHandler.trigger(this.element, EVENT.show);
      const shadowRoot = Util.findShadowRoot(this.element);
      const isInTheDom = shadowRoot !== null
        ? shadowRoot.contains(this.element)
        : this.element.ownerDocument.documentElement.contains(this.element);

      if (showEvent.defaultPrevented || !isInTheDom) {
        return;
      }

      const tip   = this.getTipElement();
      const tipId = Util.getUID(NAME);

      tip.setAttribute('id', tipId);
      this.element.setAttribute('aria-describedby', tipId);

      this.setContent();

      if (this.config.animation) {
        tip.classList.add(CLASS_NAME.fade);
      }

      const placement  = typeof this.config.placement === 'function'
        ? this.config.placement.call(this, tip, this.element)
        : this.config.placement;

      const attachment = Tooltip.getAttachment(placement);

      // Attachment Class
      this.addAttachmentClass(attachment);

      // Arrow class
      if (!this.config.arrow) {
        this.getTipElement().classList.add(CLASS_NAME.withoutArrow);
      }

      const container = this.getContainer();
      Data.setData(tip, DATA_KEY, this);

      if (!this.element.ownerDocument.documentElement.contains(this.tip)) {
        container.appendChild(tip);
      }

      EventHandler.trigger(this.element, EVENT.inserted);

      // eslint-disable-next-line no-undef
      this.popper = new Popper(this.element, tip, {
        placement: attachment,
        modifiers: {
          offset: {
            offset: this.config.offset
          },
          flip: {
            behavior: this.config.fallbackPlacement
          },
          arrow: {
            element: SELECTOR.arrow
          },
          preventOverflow: {
            boundariesElement: this.config.boundary
          }
        },
        onCreate: (data) => {
          if (data.originalPlacement !== data.placement) {
            this.handlePopperPlacementChange(data);
          }
        },
        onUpdate: (data) => this.handlePopperPlacementChange(data)
      });

      tip.classList.add(CLASS_NAME.show);

      // If this is a touch-enabled device we add extra
      // empty mouseover listeners to the body's immediate children;
      // only needed because of broken event delegation on iOS
      // https://www.quirksmode.org/blog/archives/2014/02/mouse_event_bub.html
      if ('ontouchstart' in document.documentElement) {
        Util.makeArray(document.body.children).forEach((element) => {
          EventHandler.on(element, 'mouseover', Util.noop());
        });
      }

      const complete = () => {
        if (this.config.animation) {
          this.fixTransition();
        }
        const prevHoverState = this.hoverState;
        this.hoverState     = null;

        EventHandler.trigger(this.element, EVENT.shown);

        if (prevHoverState === HOVER_STATE.out) {
          this.leave(null, this);
        }
      };

      if (this.tip.classList.contains(CLASS_NAME.fade)) {
        const transitionDuration = Util.getTransitionDurationFromElement(this.tip);
        EventHandler.one(this.tip, Util.TRANSITION_END, complete);
        Util.emulateTransitionEnd(this.tip, transitionDuration);
      } else {
        complete();
      }
    }
  }

  hide(callback) {
    const tip       = this.getTipElement();
    const complete  = () => {
      if (this.hoverState !== HOVER_STATE.show && tip.parentNode) {
        tip.parentNode.removeChild(tip);
      }

      this.cleanTipClass();
      this.element.removeAttribute('aria-describedby');
      EventHandler.trigger(this.element, EVENT.hidden);
      if (this.popper !== null) {
        this.popper.destroy();
      }

      if (callback) {
        callback();
      }
    };

    const hideEvent = EventHandler.trigger(this.element, EVENT.hide);
    if (hideEvent.defaultPrevented) {
      return;
    }

    tip.classList.remove(CLASS_NAME.show);

    // If this is a touch-enabled device we remove the extra
    // empty mouseover listeners we added for iOS support
    if ('ontouchstart' in document.documentElement) {
      Util.makeArray(document.body.children)
        .forEach((element) => EventHandler.off(element, 'mouseover', Util.noop));
    }

    this.activeTrigger[TRIGGER.click] = false;
    this.activeTrigger[TRIGGER.focus] = false;
    this.activeTrigger[TRIGGER.hover] = false;

    if (this.tip.classList.contains(CLASS_NAME.fade)) {
      const transitionDuration = Util.getTransitionDurationFromElement(tip);
      EventHandler.one(tip, Util.TRANSITION_END, complete);
      Util.emulateTransitionEnd(tip, transitionDuration);
    } else {
      complete();
    }

    this.hoverState = '';
  }

  update() {
    if (this.popper !== null) {
      this.popper.scheduleUpdate();
    }
  }

  isWithContent() {
    return Boolean(this.getTitle());
  }

  addAttachmentClass(attachment) {
    this.getTipElement().classList.add(`${CLASS_NAME.default}--${attachment}`);
  }

  getTipElement() {
    if (this.tip) {
      return this.tip;
    }

    const element = document.createElement('div');
    element.innerHTML = this.config.template;

    this.tip = element.children[0];
    return this.tip;
  }

  setContent() {
    const tip = this.getTipElement();
    this.setElementContent(SelectorEngine.findOne(SELECTOR.inner, tip), this.getTitle());
    tip.classList.remove(CLASS_NAME.fade);
    tip.classList.remove(CLASS_NAME.show);
  }

  setElementContent(element, content) {
    if (element === null) {
      return;
    }

    const html = this.config.html;
    if (typeof content === 'object' && content.nodeType) {
      // content is a DOM node
      if (html) {
        if (content.parentNode !== element) {
          element.innerHTML = '';
          element.appendChild(content);
        }
      } else {
        element.innerText = content.textContent;
      }
    } else {
      element[html ? 'innerHTML' : 'innerText'] = content;
    }
  }

  getTitle() {
    let title = this.element.getAttribute('data-original-title');

    if (!title) {
      title = typeof this.config.title === 'function'
        ? this.config.title.call(this.element)
        : this.config.title;
    }

    return title;
  }

  getContainer() {
    if (this.config.container === false) {
      return document.body;
    }

    if (Util.isElement(this.config.container)) {
      return this.config.container;
    }

    return SelectorEngine.findOne(this.config.container);
  }

  setListeners() {
    const triggers = this.config.trigger.split(' ');

    triggers.forEach((trigger) => {
      if (trigger === 'click') {
        EventHandler.on(this.element,
          EVENT.click,
          this.config.selector,
          (event) => this.toggle(event)
        );
      } else if (trigger !== TRIGGER.manual) {
        const eventIn = trigger === TRIGGER.hover
          ? EVENT.mouseenter
          : EVENT.focusin;
        const eventOut = trigger === TRIGGER.hover
          ? EVENT.mouseleave
          : EVENT.focusout;

        EventHandler.on(this.element,
          eventIn,
          this.config.selector,
          (event) => this.enter(event)
        );
        EventHandler.on(this.element,
          eventOut,
          this.config.selector,
          (event) => this.leave(event)
        );
      }
    });

    // TODO : rework when modal component will be created
    EventHandler.on(SelectorEngine.closest(this.element, '.modal'),
      'hide.nj.modal',
      () => {
        if (this.element) {
          this.hide();
        }
      }
    );

    if (this.config.selector) {
      this.config = {
        ...this.config,
        trigger: 'manual',
        selector: ''
      };
    } else {
      this.fixTitle();
    }
  }

  fixTitle() {
    const titleType = typeof this.element.getAttribute('data-original-title');

    if (this.element.getAttribute('title') || titleType !== 'string') {
      this.element.setAttribute(
        'data-original-title',
        this.element.getAttribute('title') || ''
      );

      this.element.setAttribute('title', '');
    }
  }

  enter(event, context) {
    const dataKey = DATA_KEY;
    context = context || Data.getData(event.delegateTarget, dataKey);

    if (!context) {
      context = new this.constructor(
        event.delegateTarget,
        this.getDelegateConfig()
      );
      Data.setData(event.delegateTarget, dataKey, context);
    }

    if (event) {
      context.activeTrigger[
        event.type === 'focusin' ? TRIGGER.focus : TRIGGER.hover
      ] = true;
    }

    if (context.getTipElement().classList.contains(CLASS_NAME.show) ||
      context.hoverState === HOVER_STATE.show) {
      context.hoverState = HOVER_STATE.show;
      return;
    }

    clearTimeout(context.timeout);

    context.hoverState = HOVER_STATE.show;

    if (!context.config.delay || !context.config.delay.show) {
      context.show();
      return;
    }

    context.timeout = setTimeout(() => {
      if (context._hoverState === HOVER_STATE.show) {
        context.show();
      }
    }, context.config.delay.show);
  }

  leave(event, context) {
    const dataKey = DATA_KEY;
    context = context || Data.getData(event.delegateTarget, dataKey);

    if (!context) {
      context = new this.constructor(
        event.delegateTarget,
        this.getDelegateConfig()
      );
      Data.setData(event.delegateTarget, dataKey, context);
    }

    if (event) {
      context.activeTrigger[
        event.type === 'focusout' ? TRIGGER.focus : TRIGGER.hover
      ] = false;
    }

    if (context.isWithActiveTrigger()) {
      return;
    }

    clearTimeout(context.timeout);

    context.hoverState = HOVER_STATE.out;

    if (!context.config.delay || !context.config.delay.hide) {
      context.hide();
      return;
    }

    context.timeout = setTimeout(() => {
      if (context.hoverState === HOVER_STATE.out) {
        context.hide();
      }
    }, context.config.delay.hide);
  }

  isWithActiveTrigger() {
    for (const trigger in this.activeTrigger) {
      if (this.activeTrigger[trigger]) {
        return true;
      }
    }

    return false;
  }

  getConfig(config) {
    config = {
      ...DEFAULT_OPTIONS,
      ...Manipulator.getDataAttributes(this.element),
      ...typeof config === 'object' && config ? config : {}
    };

    if (typeof config.delay === 'number') {
      config.delay = {
        show: config.delay,
        hide: config.delay
      };
    }

    if (typeof config.title === 'number') {
      config.title = config.title.toString();
    }

    if (typeof config.content === 'number') {
      config.content = config.content.toString();
    }

    Util.typeCheckConfig(
      NAME,
      config,
      DEFAULT_TYPE
    );

    return config;
  }

  getDelegateConfig() {
    const config = {};

    if (this.config) {
      for (const key in this.config) {
        if (DEFAULT_OPTIONS[key] !== this.config[key]) {
          config[key] = this.config[key];
        }
      }
    }

    return config;
  }

  cleanTipClass() {
    const tip = this.getTipElement();
    const tabClass = tip.getAttribute('class').match(NJCLS_PREFIX_REGEX);
    if (tabClass !== null && tabClass.length) {
      tabClass
        .map((token) => token.trim())
        .forEach((tClass) => tip.classList.remove(tClass));
    }
  }

  handlePopperPlacementChange(popperData) {
    const popperInstance = popperData.instance;
    this.tip = popperInstance.popper;
    this.cleanTipClass();
    this.addAttachmentClass(Tooltip.getAttachment(popperData.placement));
  }

  fixTransition() {
    const tip = this.getTipElement();
    const initConfigAnimation = this.config.animation;
    if (tip.getAttribute('x-placement') !== null) {
      return;
    }
    tip.classList.remove(CLASS_NAME.fade);
    this.config.animation = false;
    this.hide();
    this.show();
    this.config.animation = initConfigAnimation;
  }

  static getAttachment(placement) {
    return ATTACHMENT_MAP[placement.toUpperCase()];
  }

  static getInstance(element) {
    return Data.getData(element, DATA_KEY);
  }

  static init() {
    // eslint-disable-next-line
    console.info(`
      [NJ Warning]: Tooltips are opt-in for performance reasons, so you must initialize them yourself.
      Read the doc : https://design.engie.com/design-system/components/tooltip/#example-enable-tooltips-everywhere
    `);
    return null;
  }

  static selector = SELECTOR.default;
}

export default Tooltip;
